import React, { FC, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CloseIcon from '@material-ui/icons/Close';
import axios, { CancelTokenSource } from 'axios';

import { CLIENT_BASE_URL, GET_SERVICE_ADDRESS_BY_CLIENT_ID_URL, TRANSFER_SERVICE_URL } from 'constants/url';
import PasswordConfirmation from 'components/PasswordConfirmation';
import LoadingButtonIndicator from 'components/LoadingButtonIndicator';

interface Props {
  open: boolean;
  serviceId: number;
  serviceTitle: string;
  currentClientId: number;
  currentClientName: string;
  handleClose(): void;
  handleSnackbar(variant: 'success' | 'error', message: string, isCountdown?: boolean): void;
  fetchData(): void;
}

const useStyles = makeStyles((theme: Theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    fontSize: 20
  }
}));

const TransferQuotationDialog: FC<Props> = props => {
  const classes = useStyles();
  const { open, serviceId, serviceTitle, currentClientId, currentClientName, handleClose, handleSnackbar, fetchData } = props;

  const [isLoadingClient, setIsLoadingClient] = useState<boolean>(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState<boolean>(false);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [serviceAddressMaster, setServiceAddressMaster] = useState<Select[]>([]);
  const [serviceAddressId, setServiceAddressId] = useState<number>(0);
  const [openPasswordDialog, setOpenPasswordDialog] = useState<boolean>(false);

  const searchClient = async (query: string) => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

    try {
      setIsLoadingClient(true);

      const params = new URLSearchParams();
      params.append('q', query);
      params.append('s', '0');
      params.append('l', '15');

      const { data } = await axios.get(`${CLIENT_BASE_URL}?${params.toString()}`, { cancelToken: cancelTokenSource.token });

      setClientOptions(data.clients.filter((client: ClientOption) => client.id !== currentClientId));
    } catch (err) {
      console.log(err);
    }
    setIsLoadingClient(false);
  };

  const getServiceAddresses = async (clientId: number) => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

    try {
      setIsLoadingAddress(true);

      const { data } = await axios.get(`${GET_SERVICE_ADDRESS_BY_CLIENT_ID_URL(clientId)}`, { cancelToken: cancelTokenSource.token });

      const serviceAddressData: Select[] = [];
      data.serviceAddresses.map((value: any) => {
        return serviceAddressData.push({ id: value.id, name: value.address });
      });

      setServiceAddressMaster(serviceAddressData);
      setServiceAddressId(serviceAddressData.length > 0 ? serviceAddressData[0].id : 0);
    } catch (err) {
      console.log(err);
      handleSnackbar('error', 'Failed to fetch service addresses');
    }
    setIsLoadingAddress(false);
  };

  const handleChangeClient = (value: ClientOption | null) => {
    setSelectedClient(value);
    setServiceAddressMaster([]);
    setServiceAddressId(0);

    if (value) {
      getServiceAddresses(value.id);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth='sm'>
      <DialogTitle>
        <Typography variant='h5'>Transfer Quotation</Typography>
        <IconButton size='small' onClick={handleClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant='body1'>
              Transfer quotation <b>{serviceTitle} (#{serviceId})</b> from client <b>{currentClientName} (#{currentClientId})</b> to:
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              id='transferClient'
              options={clientOptions}
              getOptionLabel={option => `${option.name} (#${option.id})`}
              value={selectedClient}
              onInputChange={(_, value, reason) => {
                if (reason === 'input' && value.length > 2) {
                  searchClient(value);
                }
              }}
              onChange={(_, value) => handleChangeClient(value)}
              autoHighlight
              loading={isLoadingClient}
              loadingText='Searching…'
              noOptionsText='No client found'
              renderOption={(option, { inputValue }) => (
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    {option.name.split(new RegExp(`(${inputValue})`, 'gi')).map((part: any, index: number) =>
                      part.toLowerCase() === inputValue.toLowerCase() ? (
                        <span key={index} style={{ color: '#53A0BE' }}>
                          {part}
                        </span>
                      ) : (
                        <span key={index}>{part}</span>
                      )
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='caption'>
                      #{option.id} - {option.firstServiceAddress}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  margin='dense'
                  variant='outlined'
                  label='Transfer to Client'
                  placeholder='Search client by name'
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              required
              fullWidth
              margin='dense'
              variant='outlined'
              id='transferServiceAddress'
              label='Service Address'
              value={serviceAddressId === 0 ? '' : serviceAddressId}
              disabled={!selectedClient || isLoadingAddress}
              onChange={event => setServiceAddressId(Number(event.target.value))}
            >
              {serviceAddressMaster.map(address => (
                <MenuItem key={address.id} value={address.id}>
                  {address.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant='contained' disableElevation onClick={handleClose} disabled={isLoadingClient || isLoadingAddress}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          disableElevation
          disabled={!selectedClient || serviceAddressId === 0 || isLoadingClient || isLoadingAddress}
          onClick={() => setOpenPasswordDialog(true)}
        >
          Save
          <LoadingButtonIndicator isLoading={isLoadingAddress} />
        </Button>
      </DialogActions>
      {openPasswordDialog && selectedClient && (
        <PasswordConfirmation
          open={openPasswordDialog}
          handleClose={() => setOpenPasswordDialog(false)}
          url={TRANSFER_SERVICE_URL}
          isEdit
          editData={{ serviceIds: [serviceId], clientId: selectedClient.id, serviceAddressId }}
          title='Confirm Quotation Transfer'
          message={`transfer quotation #${serviceId} to ${selectedClient.name} (#${selectedClient.id})`}
          isCountdown={false}
          handleSnackbar={handleSnackbar}
          fetchData={() => {
            fetchData();
            handleClose();
          }}
        />
      )}
    </Dialog>
  );
};

export default TransferQuotationDialog;
